// 📁 scholarship-frontend/src/pages/Login.tsx
// CTO Recovery Prompt — Canonical Corporate Login Screen
//
// Invariants (LOCKED):
// - Uses axiosInstance → always hits /api/v1 surface
// - Does NOT send X-Tenant-ID on login (portfolio-first model)
// - Resolves tenant list from /portfolio/tenants (shape: { tenants: [...] })
// - Persists tenant_id + tenant_slug into sessionStorage ONLY after server-backed tenant resolution
// - Never "pre-writes" tenant_slug from URL (prevents tenant slug poisoning)
// - Redirects purely by role + tenant slug (no hard-coded tenant ids)
// - If URL tenantSlug disagrees with server-assigned tenantSlug, server wins (fail-safe redirect)

import React, { FormEvent, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { isAxiosError } from "axios";

import axiosInstance from "@/utils/axiosInstance";
import { getTenantSlugFromStorage, getUserFromToken } from "@/utils/auth";

interface PortfolioTenant {
  id: number;
  name?: string; // backend may return only {id, slug}
  slug: string;
}

function normalizeSlug(raw?: string | null): string | null {
  if (!raw) return null;
  const s = raw.trim().toLowerCase();
  return s ? s : null;
}

function normalizeHumanEntry(raw: string): string {
  return raw.trim().replace(/\s+/g, " ");
}

function extractErrorMessage(err: unknown, fallback: string): string {
  if (isAxiosError(err)) {
    const detail = err.response?.data?.detail;
    if (typeof detail === "string" && detail.trim()) {
      return detail;
    }
    if (Array.isArray(detail) && detail.length > 0) {
      return "Please check the highlighted input and try again.";
    }
  }
  return err instanceof Error && err.message ? err.message : fallback;
}

const Login: React.FC = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [recoveryOpen, setRecoveryOpen] = useState(false);
  const [recoveryIdentifier, setRecoveryIdentifier] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [recoveryMessage, setRecoveryMessage] = useState<string | null>(null);
  const [devResetCode, setDevResetCode] = useState<string | null>(null);
  const [pendingTenants, setPendingTenants] = useState<PortfolioTenant[]>([]);
  const [pendingRole, setPendingRole] = useState<string | null>(null);
  const [loginError, setLoginError] = useState<string | null>(null);

  const navigate = useNavigate();
  const { tenantSlug } = useParams<{ tenantSlug?: string }>();

  // URL slug is used for routing *only* (not persisted pre-login).
  const urlSlug = useMemo(() => normalizeSlug(tenantSlug), [tenantSlug]);

  const completeTenantSelection = (
    selectedTenant: PortfolioTenant,
    role: string
  ) => {
    if (!selectedTenant?.slug || !normalizeSlug(selectedTenant.slug)) {
      throw new Error("Tenant resolution returned an invalid tenant slug.");
    }

    const serverSlug = normalizeSlug(selectedTenant.slug)!;

    // Persist tenant_id and tenant_slug ONLY here (server-backed canonical write point)
    sessionStorage.setItem("tenant_id", String(selectedTenant.id));
    sessionStorage.setItem("tenant_slug", serverSlug);

    // Route slug resolution (server wins; URL/storage are fallbacks only)
    // NOTE: We do NOT trust URL slug for persistence; it’s routing convenience only.
    const slugForRoute =
      serverSlug || urlSlug || normalizeSlug(getTenantSlugFromStorage()) || "default";

    // If URL slug exists and disagrees with server assignment, correct the URL silently.
    // This prevents "slug poisoning" and eliminates confusing behavior.
    if (urlSlug && urlSlug !== serverSlug) {
      console.warn(
        `URL tenantSlug "${urlSlug}" does not match server-assigned "${serverSlug}". Redirecting to server slug.`
      );
    }

    const redirectMap: Record<string, string> = {
      ddo: `/${slugForRoute}/ddo/dashboard`,
      pm: `/${slugForRoute}/pm/dashboard`,
      pc: `/${slugForRoute}/pc/dashboard`,
      finance: `/${slugForRoute}/finance/dashboard`,
      management: `/${slugForRoute}/management/dashboard`,
      client: `/${slugForRoute}/client/dashboard`,
      admin: `/${slugForRoute}/admin/dashboard`,
      super_admin: `/${slugForRoute}/admin/tenant-settings`,
    };

    toast.success("Login successful!");
    navigate(redirectMap[role] || `/${slugForRoute}/login`, { replace: true });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setLoginError(null);
    setPendingTenants([]);
    setPendingRole(null);

    try {
      const loginIdentifier = normalizeHumanEntry(username);
      if (!loginIdentifier) {
        throw new Error("Username or email is required.");
      }

      // 1) Call canonical /auth/login (no X-Tenant-ID on login)
      const loginRes = await axiosInstance.post<{
        access_token?: string;
        refresh_token?: string;
        token_type?: string;
        role?: string;
      }>("/auth/login", { username: loginIdentifier, password });

      const data = loginRes.data;

      if (!data.access_token || !data.refresh_token) {
        throw new Error("Invalid response. Missing access/refresh token.");
      }

      // 2) Store tokens (session-scoped)
      sessionStorage.setItem("access_token", data.access_token);
      sessionStorage.setItem("refresh_token", data.refresh_token);

      // 3) Decode token to get role / user id (JWT is frontend truth)
      const user = getUserFromToken();
      if (!user?.role || user.sub === undefined || user.sub === null) {
        throw new Error("Token decode failed.");
      }

      // 4) Resolve tenant assignments from portfolio API (system-scope, assignment-ledger truth)
      const tenantsRes = await axiosInstance.get<{ tenants: PortfolioTenant[] }>(
        "/portfolio/tenants"
      );

      const tenants = tenantsRes.data?.tenants ?? [];

      if (!Array.isArray(tenants)) {
        throw new Error("Portfolio API did not return a tenant list.");
      }
      if (tenants.length === 0) {
        throw new Error("No active tenant assignments found for this user.");
      }

      // 5) Select active tenant workspace.
      let selectedTenant: PortfolioTenant | null = null;

      if (tenants.length === 1) {
        selectedTenant = tenants[0];
      } else if (user.role === "super_admin") {
        selectedTenant =
          tenants.find((tenant) => normalizeSlug(tenant.slug) === "default") ??
          tenants[0];
      } else {
        sessionStorage.removeItem("tenant_id");
        sessionStorage.removeItem("tenant_slug");
        toast.info("Opening read-only all-tenant operations view.");
        navigate("/operations/overview", { replace: true });
        return;
      }

      completeTenantSelection(selectedTenant, user.role);
    } catch (err) {
      console.error("Login error:", err);

      const message = extractErrorMessage(
        err,
        "Login failed. Please check your username or email and password."
      );
      setLoginError(message);
      toast.error(message);

      // Fail-loud: clear partial session to avoid half-authenticated state
      sessionStorage.removeItem("access_token");
      sessionStorage.removeItem("refresh_token");
      sessionStorage.removeItem("tenant_id");
      sessionStorage.removeItem("tenant_slug");
    } finally {
      setLoading(false);
    }
  };

  const requestPasswordReset = async () => {
    const slug = urlSlug || "default";
    const identifier = normalizeHumanEntry(recoveryIdentifier);
    setRecoveryLoading(true);
    setRecoveryMessage(null);
    setDevResetCode(null);
    try {
      const res = await axiosInstance.post<{
        ok: boolean;
        message: string;
        dev_reset_code?: string | null;
      }>("/auth/forgot-password/request", {
        tenant_slug: slug,
        username_or_email: identifier,
      });
      setRecoveryMessage(res.data.message);
      setDevResetCode(res.data.dev_reset_code ?? null);
    } catch (err) {
      setRecoveryMessage(
        extractErrorMessage(err, "Could not start password recovery.")
      );
    } finally {
      setRecoveryLoading(false);
    }
  };

  const resetForgottenPassword = async () => {
    const slug = urlSlug || "default";
    const identifier = normalizeHumanEntry(recoveryIdentifier);
    setRecoveryLoading(true);
    setRecoveryMessage(null);
    try {
      const res = await axiosInstance.post<{ message: string }>(
        "/auth/forgot-password/reset",
        {
          tenant_slug: slug,
          username_or_email: identifier,
          otp: resetCode,
          new_password: newPassword,
        }
      );
      setRecoveryMessage(res.data.message);
      setPassword("");
      setResetCode("");
      setNewPassword("");
    } catch (err) {
      setRecoveryMessage(extractErrorMessage(err, "Could not reset password."));
    } finally {
      setRecoveryLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto mt-20 p-6 bg-white shadow rounded">
      <h2 className="text-2xl font-semibold mb-2 text-center">
        Scholarship Admin Login
      </h2>
      <div className="text-xs text-muted-foreground text-center mb-4">
        Tenant (URL): <span className="font-medium">{urlSlug ?? "—"}</span>
      </div>

      {pendingTenants.length > 0 && pendingRole ? (
        <div className="mb-6 rounded border border-amber-200 bg-amber-50 p-4">
          <h3 className="text-sm font-semibold text-amber-950">
            Choose tenant workspace
          </h3>
          <p className="mt-1 text-xs text-amber-900">
            This account has access to multiple tenants. Select one before continuing.
          </p>
          <div className="mt-3 space-y-2">
            {pendingTenants.map((tenant) => (
              <button
                key={tenant.id}
                type="button"
                onClick={() => completeTenantSelection(tenant, pendingRole)}
                className="w-full rounded border border-amber-300 bg-white px-3 py-2 text-left text-sm hover:bg-amber-100"
              >
                <span className="block font-medium text-amber-950">
                  {tenant.name || tenant.slug}
                </span>
                <span className="text-xs text-amber-800">{tenant.slug}</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <form onSubmit={handleSubmit}>
        {loginError ? (
          <div className="mb-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            {loginError}
          </div>
        ) : null}

        <label className="block mb-2 font-medium">Username</label>
        <input
          type="text"
          required
          value={username}
          onChange={(e) => {
            setUsername(e.target.value);
            setLoginError(null);
          }}
          className="w-full border px-3 py-2 rounded mb-4"
          autoComplete="username"
        />

        <label className="block mb-2 font-medium">Password</label>
        <input
          type="password"
          required
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            setLoginError(null);
          }}
          className="w-full border px-3 py-2 rounded mb-6"
          autoComplete="current-password"
        />

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700 disabled:opacity-60"
        >
          {loading ? "Logging in..." : "Login"}
        </button>
      </form>

      <div className="mt-4 border-t pt-4">
        <button
          type="button"
          onClick={() => {
            setRecoveryOpen((current) => !current);
            setRecoveryIdentifier(username);
            setRecoveryMessage(null);
            setDevResetCode(null);
          }}
          className="text-sm font-medium text-blue-700 hover:text-blue-900"
        >
          Forgot password?
        </button>

        {recoveryOpen ? (
          <div className="mt-4 rounded border border-slate-200 bg-slate-50 p-4">
            <label className="block text-sm font-medium">Username or email</label>
            <input
              type="text"
              value={recoveryIdentifier}
              onChange={(event) => setRecoveryIdentifier(event.target.value)}
              className="mt-1 w-full rounded border px-3 py-2 text-sm"
              autoComplete="username"
            />
            <button
              type="button"
              onClick={requestPasswordReset}
              disabled={recoveryLoading || recoveryIdentifier.trim().length < 3}
              className="mt-3 w-full rounded bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              Send reset code
            </button>

            {devResetCode ? (
              <div className="mt-3 rounded border border-amber-200 bg-amber-50 p-3 text-xs text-amber-950">
                Local development reset code: <span className="font-semibold">{devResetCode}</span>
              </div>
            ) : null}

            <div className="mt-4 grid gap-3">
              <label className="block text-sm font-medium">
                Reset code
                <input
                  type="text"
                  value={resetCode}
                  onChange={(event) => setResetCode(event.target.value)}
                  className="mt-1 w-full rounded border px-3 py-2 text-sm"
                  inputMode="numeric"
                  maxLength={6}
                />
              </label>
              <label className="block text-sm font-medium">
                New password
                <input
                  type="password"
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  className="mt-1 w-full rounded border px-3 py-2 text-sm"
                  autoComplete="new-password"
                  maxLength={128}
                />
                <span className="mt-1 block text-xs font-normal text-slate-600">
                  Minimum 10 characters, maximum 128. Include at least one letter and one number.
                  Uppercase, lowercase, and special characters are allowed but not required.
                </span>
              </label>
              <button
                type="button"
                onClick={resetForgottenPassword}
                disabled={
                  recoveryLoading ||
                  recoveryIdentifier.trim().length < 3 ||
                  resetCode.length !== 6 ||
                  newPassword.length < 10 ||
                  newPassword.length > 128
                }
                className="w-full rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                Reset password
              </button>
              {newPassword.length > 0 && newPassword.length < 10 ? (
                <p className="text-xs text-amber-800">
                  Password must be at least 10 characters.
                </p>
              ) : null}
              {newPassword.length > 128 ? (
                <p className="text-xs text-amber-800">
                  Password must be 128 characters or fewer.
                </p>
              ) : null}
            </div>

            {recoveryMessage ? (
              <p className="mt-3 text-sm text-slate-700">{recoveryMessage}</p>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default Login;
